// SCB Business Net bulk-transfer file builder.
//
// This is the Export macro of SCB's CPX Toolkit .xlsm, in JS. It replaces the
// copy-into-Excel-and-run-the-macro step: rows in, the exact pipe-delimited .txt
// out. The Python reference (scripts/scb_bulk/scb_bulk.py) reproduces real
// exports BYTE-IDENTICALLY — both a doctor-fee run (109 txns, RFT) and an
// SCB-internal one (88 txns, 3PT). Verify any change here against that script
// and against knowledge/payroll/README.md; the format spec is in
// scripts/scb_bulk/README.md.
//
// UTF-8, no BOM, CRLF, NO trailing newline. Four record types:
//   HEADER|<file ref>|<system ref>                                    3 fields
//   BCHDET|<batch>|<product>|<YYYYMMDD>|<debit>|<fee>|<total>|<n>||  10 fields
//   TXNDET|…                                                         28 fields
//   TRAILR|1|<n>|<total>                                              4 fields

const DELIM = '|'
export const EOL = '\r\n'

// The FULL bank-name → clearing-code table from the toolkit's hidden Master_data
// sheet (TBBankBNT1, rows 126–162). Generated from scripts/scb_bulk/banks.json —
// regenerate both together.
//
// ⚠️ Use the WHOLE table, not the eight values of the `Employee.bank` enum. That
// enum is complete for employees by construction; DOCTOR bank names are free text
// (`Doctor.bank_track.bank_name`), so any of these 37 can turn up. Resolving
// doctors against the 8-value subset silently dropped แพร์ ชวัลรัตณ์ and her
// ฿50,047.78 in 2026-08 — she banks with ไทยเครดิต, which is right here as 071.
const BANK_TABLE = {
  'ธนาคารแห่งประเทศไทย': '001',
  'กรุงเทพ': '002',
  'กสิกรไทย': '004',
  'กรุงไทย': '006',
  'เจพีมอร์แกน เชส': '008',
  'โอซีบีซี': '009',
  'ทหารไทยธนชาต จำกัด (มหาชน)': '011',
  'ไทยพาณิชย์': '014',
  'ซิตี้แบงค์': '017',
  'ซูมิโตโม มิตซุย แบงกิ้ง คอร์ปอเรชั่น': '018',
  'สแตนดาร์ดชาร์เตอร์ด (ไทย)': '020',
  'ซีไอเอ็มบี': '022',
  'อาร์ เอช บี จำกัด': '023',
  'ยูโอบี': '024',
  'กรุงศรีอยุธยา': '025',
  'เมกะ สากลพาณิชย์ จำกัด': '026',
  'แห่งอเมริกา เนชั่นแนล แอสโซซิเอชั่น': '027',
  'อินเดียน โอเวอร์ซีส์': '029',
  'ออมสิน': '030',
  'ฮ่องกงและเซี่ยงไฮ้แบงกิ้งคอร์ปอเรชั่น จำกัด': '031',
  'ดอยซ์แบงค์': '032',
  'อาคารสงเคราะห์': '033',
  'เพื่อการเกษตรและสหกรณ์': '034',
  'ธนาคารเพื่อการส่งออกและนำเข้าแห่งประเทศไทย': '035',
  'มิตซูโฮ คอร์ปอเรต จำกัด': '039',
  'บีเอ็นพี พารีบาส์ สาขากรุงเทพฯ': '045',
  'ธนาคารแห่งประเทศจีน (ไทย)': '052',
  'ธนชาต': '065',
  'อิสลามแห่งประเทศไทย': '066',
  'ทิสโก้': '067',
  'เกียรตินาคิน': '069',
  'ไอซีบีซี (ไทย)': '070',
  'ไทยเครดิต เพื่อรายย่อย': '071',
  'แลนด์แอนด์เฮ้าส์': '073',
  'เอเอ็นแซด (ไทย)': '079',
  'ซูมิโตโม มิตซุย ทรัสต์ (ไทย)': '080',
  'ธนาคารพัฒนาวิสาหกิจขนาดกลางและขนาดย่อมแห่งประเทศไทย': '098',
}

// Spellings the data uses that the toolkit's own strings do not. Both of these
// are ttb — TMB and ธนชาต merged in 2021 and 011 carried over — so they are
// ALIASES ONTO A TABLE CODE, never a new code. `ทหารไทย` is the Employee.bank
// enum value; `ทหารไทยธนชาต` is what doctor records carry (17 of 204 in 2026-08).
//
// The last three are the SELLER side. `seller.bank` is written by the Combobox in
// SellerEditor from `src/data/banks.js`, and that list spells three banks with ก
// where the toolkit's table uses ค — ซิตี้แบงก์/ค์, ดอยซ์แบงก์/ค์, มิซูโฮ/มิตซูโฮ.
// No seller carries the ก spelling TODAY (measured 2026-09-13: the 318 sellers
// with a bank use only 8 distinct values, all of which resolve), but the picker
// writes it from now on — so a vendor edited tomorrow would drop out of the file
// silently, exactly the way แพร์ ชวัลรัตณ์ did. Aliases, not new codes.
const BANK_ALIAS = {
  'ทหารไทย': '011',
  'ทหารไทยธนชาต': '011',
  'ซิตี้แบงก์': '017',
  'ดอยซ์แบงก์': '032',
  'มิซูโฮ คอร์ปอเรต จำกัด': '039',
}

export const BANK_CODE = { ...BANK_TABLE, ...BANK_ALIAS }

export const SCB_BANK = 'ไทยพาณิชย์'

// The two products, and the branch code each one pins into field 5. 3PT is
// SCB→SCB, RFT is Other Bank Transfer (ORFT). Same pair the doctor-fee files
// have always used; change here and in scripts/scb_bulk/README.md together.
export const PRODUCT = {
  scb: { code: '3PT', branch: '0111' },
  other: { code: 'RFT', branch: '0000' },
}

// FormatNumber(value, 2) — fixed 2 decimals, no thousands separator.
// Amounts arrive from Xano as strings ("15517.00") so they keep their precision;
// summing goes through integer satang to avoid float drift on 100+ rows.
export function satang(value) {
  const n = Math.round(Number(value) * 100)
  return Number.isFinite(n) ? n : 0
}

export function money(satangTotal) {
  const sign = satangTotal < 0 ? '-' : ''
  const abs = Math.abs(satangTotal)
  return `${sign}${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, '0')}`
}

const flag = (v) => (String(v ?? '').trim() ? 'Y' : 'N')

// DDMMYYHHMMSS — what generateCustomerBatchReferanceName() produces from Now().
// The macro invents this too; it carries no meaning beyond being unique.
export function batchRef(now = new Date()) {
  const p = (n) => String(n).padStart(2, '0')
  return [
    p(now.getDate()), p(now.getMonth() + 1), p(now.getFullYear() % 100),
    p(now.getHours()), p(now.getMinutes()), p(now.getSeconds()),
  ].join('')
}

// `<company no>-<uuid>` clipped to 20 chars — the convention the existing Xano
// export already writes, so refs stay recognisable across both paths.
export function txnRef(companyNo) {
  const uuid = (globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`)
  return `${companyNo}-${uuid}`.slice(0, 20)
}

// One TXNDET line. Field numbers are the macro's rowArr() indices — see the
// table in scripts/scb_bulk/README.md before touching any of them.
function txnLine(row, product) {
  const phone = String(row.phone || '').replace(/[-\s]/g, '')
  const email = String(row.email || '').trim()
  const f = new Array(28).fill('')
  f[0] = 'TXNDET'
  f[1] = row.ref
  f[2] = String(row.bank_num || '').trim()
  f[4] = BANK_CODE[String(row.bank || '').trim()] || ''
  f[5] = product.branch
  f[6] = money(satang(row.net))
  f[8] = 'OUR' // fee charged to the payer
  f[9] = flag(phone)
  f[10] = phone
  f[11] = flag(email)
  f[12] = email
  f[13] = String(row.name || '').trim()
  f[17] = 'N' // WHT certificate — the toolkit refuses WHT on payroll products
  f[20] = flag(email) // payment advice
  f[21] = 'N' // invoice detail
  f[25] = email ? 'E' : 'N' // WHT delivery method
  f[26] = email
  f[27] = row.remark || ''
  return f.join(DELIM)
}

/**
 * Build one file.
 *   rows        — [{ ref, bank, bank_num, name, net, phone, email, remark }]
 *   product     — PRODUCT.scb | PRODUCT.other
 *   debitAccount— the company's Branch.Company.SCB_bankaccount
 *   valueDate   — 'YYYY-MM-DD' (the transfer date)
 *   ref         — batch reference, DDMMYYHHMMSS
 */
export function buildFile({ rows, product, debitAccount, valueDate, ref }) {
  const total = rows.reduce((sum, r) => sum + satang(r.net), 0)
  const date = valueDate.replace(/-/g, '')
  const lines = [
    ['HEADER', ref + product.code, ''].join(DELIM),
    ['BCHDET', ref, product.code, date, debitAccount, debitAccount,
      money(total), String(rows.length), '', ''].join(DELIM),
    ...rows.map((r) => txnLine(r, product)),
    ['TRAILR', '1', String(rows.length), money(total)].join(DELIM),
  ]
  return { text: lines.join(EOL), total, count: rows.length }
}

// The file names accounting already knows, from Drive
// เงินเดือนพนักงาน/69/txt/DONE. Keyed by Branch.Company.no.
export const COMPANY_FILE = {
  1: 'SSDC',
  2: 'SSDC-asoke',
  3: 'SSDC_Ratchayothin',
  4: 'SSDC-SS6',
  5: 'SSDC_Westville',
}

export const ISSUE_LABEL = {
  net_not_positive: 'ยอดสุทธิไม่เกิน 0',
  no_bank: 'ไม่ได้ระบุธนาคาร',
  no_account: 'ไม่มีเลขที่บัญชี',
  no_debit_account: 'บริษัทไม่มีบัญชีตัดเงิน SCB',
  unknown_bank: 'ไม่รู้จักธนาคารนี้',
  // ค่าใช้จ่าย only. `no_seller` is the vendor typed as free text and never
  // registered, so there is no seller master row to hold a bank account at all;
  // `already_paid` is someone ticking จ่ายแล้ว between the page load and the
  // export (Xano re-reads `paid`, the browser's copy of the list does not).
  no_seller: 'ยังไม่ได้ผูกกับบริษัทในระบบ',
  already_paid: 'ถูกทำเครื่องหมายจ่ายแล้ว',
}

/**
 * Split a month's rows into the ten files: five companies × (ไทยพาณิชย์ | อื่นๆ).
 * A group with no transferable row produces no file.
 *
 * Returns { files: [{ name, text, count, total, companyNo, companyName,
 *   productCode, debitAccount }], skipped: [{ …row, issue }], grandTotal }
 */
export function buildTransferFiles({ rows, year, month, valueDate, now = new Date() }) {
  const remark = `เงินเดือน ${month}-${year}`
  const ref = batchRef(now)
  const skipped = []
  const groups = new Map()

  for (const row of rows) {
    const issue = row.issue || (BANK_CODE[String(row.bank || '').trim()] ? '' : 'unknown_bank')
    if (issue) { skipped.push({ ...row, issue }); continue }

    const isScb = String(row.bank).trim() === SCB_BANK
    const key = `${row.company_no}:${isScb ? 'scb' : 'other'}`
    if (!groups.has(key)) {
      groups.set(key, {
        companyNo: row.company_no,
        companyName: row.company_name,
        debitAccount: row.debit_account,
        product: isScb ? PRODUCT.scb : PRODUCT.other,
        suffix: isScb ? 'SCB' : 'nonSCB',
        rows: [],
      })
    }
    groups.get(key).rows.push({ ...row, ref: txnRef(row.company_no), remark })
  }

  const files = []
  let grandTotal = 0
  // company, then SCB before nonSCB — the order the files are listed in Drive.
  const ordered = [...groups.values()].sort(
    (a, b) => a.companyNo - b.companyNo || (a.suffix === 'SCB' ? -1 : 1),
  )
  for (const g of ordered) {
    const built = buildFile({
      rows: g.rows,
      product: g.product,
      debitAccount: g.debitAccount,
      valueDate,
      ref,
    })
    grandTotal += built.total
    files.push({
      name: `${COMPANY_FILE[g.companyNo] || `company${g.companyNo}`}-${g.suffix}.txt`,
      text: built.text,
      count: built.count,
      total: built.total,
      companyNo: g.companyNo,
      companyName: g.companyName,
      productCode: g.product.code,
      debitAccount: g.debitAccount,
    })
  }

  return { files, skipped, grandTotal }
}

/* ── ค่าใช้จ่าย (expense) ───────────────────────────────────────────────────
 * Same five companies and the same two products as payroll, but the payee is a
 * VENDOR, not an employee, and there is one transfer line per EXPENSE — not one
 * per vendor. Three unpaid invoices from เซ็นทรัลพัฒนา leave three debits on the
 * statement, each matching one row in ค่าใช้จ่าย, which is what makes the export
 * reconcilable afterwards.
 *
 * Rows come from POST /api/expense/scb-bulk (Xano fn 223) already carrying the
 * debit side (Branch.Company) and the credit side (the seller MASTER's bank —
 * the seller snapshot frozen on the expense has no bank fields).
 */

// SCB's remark field is short and the toolkit's Validation.bas is not
// reimplemented here, so a long Thai `detail` is clipped rather than risking a
// rejected upload. 35 is the conventional ORFT remark length — if Business Net
// turns out to accept more, this constant is the only place to raise.
export const EXPENSE_REMARK_MAX = 35

// Thai combining vowels and tone marks (U+0E31, U+0E34–U+0E3A, U+0E47–U+0E4E)
// are their own code points, so a blind slice can leave one orphaned on the end.
const THAI_COMBINING_TAIL = /[ัิ-ฺ็-๎]+$/

export function clipRemark(text, max = EXPENSE_REMARK_MAX) {
  const s = String(text || '').replace(/\s+/g, ' ').trim()
  if (s.length <= max) return s
  return s.slice(0, max).replace(THAI_COMBINING_TAIL, '').trim()
}

// `E<expense id>-<uuid>` clipped to 20 — the ref is the only field that travels
// to the bank statement and back, so it carries the expense id rather than the
// company number the payroll refs use. Regenerated on every download: a re-run
// must not reuse a reference the bank has already seen.
export function expenseRef(expenseId) {
  const uuid = (globalThis.crypto?.randomUUID?.() || `${Date.now()}${Math.random()}`).replace(/-/g, '')
  return `E${expenseId}-${uuid}`.slice(0, 20)
}

/**
 * Split unpaid expenses into up to ten files: five companies × (ไทยพาณิชย์ | อื่นๆ).
 * A group with no transferable row produces no file.
 *
 *   rows         — from /api/expense/scb-bulk
 *   periodLabel  — what the page was filtered to ('2026-09', '2026', 'ทั้งหมด');
 *                  prefixes each file name the way the doctor-fee files are named
 *   valueDate    — 'YYYY-MM-DD', the day the money leaves
 *
 * The vendor's email ALWAYS goes into the file, so SCB always mails them a
 * payment advice. This used to be an opt-in checkbox in ExpenseScbModal and is
 * now unconditional (Ram, 2026-09-14): the advice is what lets the vendor match
 * the credit to the invoice, and an operator leaving it off meant a transfer
 * landing unannounced. A vendor with no email on the seller master simply gets
 * nothing — `flag()` writes 'N' and the bank has nowhere to send it.
 *
 * Returns { files: [{ name, text, count, total, companyNo, companyName,
 *   productCode, debitAccount, rows }], skipped: [{ …row, issue }], grandTotal }
 */
export function buildExpenseTransferFiles({
  rows, periodLabel = '', valueDate, now = new Date(),
}) {
  const ref = batchRef(now)
  const skipped = []
  const groups = new Map()

  for (const row of rows || []) {
    // The server's `issue` wins (it re-read `paid` and the seller master); the
    // bank-name check is the one thing only the client's table can answer.
    const issue = row.issue || (BANK_CODE[String(row.bank || '').trim()] ? '' : 'unknown_bank')
    if (issue) { skipped.push({ ...row, issue }); continue }

    const isScb = String(row.bank).trim() === SCB_BANK
    const key = `${row.company_no}:${isScb ? 'scb' : 'other'}`
    if (!groups.has(key)) {
      groups.set(key, {
        companyNo: row.company_no,
        companyName: row.company_name,
        debitAccount: row.debit_account,
        product: isScb ? PRODUCT.scb : PRODUCT.other,
        suffix: isScb ? 'SCB' : 'nonSCB',
        rows: [],
      })
    }
    groups.get(key).rows.push({
      ...row,
      ref: expenseRef(row.expense_id),
      remark: clipRemark(row.detail),
      // txnLine turns a present email into the e-advice + e-WHT flags.
      email: row.email || '',
      phone: '', // no SMS to a vendor — it costs per message and lands on a switchboard
    })
  }

  const files = []
  let grandTotal = 0
  // company, then SCB before nonSCB — the order the files are listed in Drive.
  const ordered = [...groups.values()].sort(
    (a, b) => a.companyNo - b.companyNo || (a.suffix === 'SCB' ? -1 : 1),
  )
  const prefix = periodLabel ? `${periodLabel} ` : ''
  for (const g of ordered) {
    const built = buildFile({
      rows: g.rows,
      product: g.product,
      debitAccount: g.debitAccount,
      valueDate,
      ref,
    })
    grandTotal += built.total
    files.push({
      name: `${prefix}${COMPANY_FILE[g.companyNo] || `company${g.companyNo}`}-${g.suffix}.txt`,
      text: built.text,
      count: built.count,
      total: built.total,
      companyNo: g.companyNo,
      companyName: g.companyName,
      productCode: g.product.code,
      debitAccount: g.debitAccount,
      rows: g.rows,
    })
  }

  return { files, skipped, grandTotal }
}
